import React, { ChangeEvent, useState } from 'react';

import { Button, Input, Label, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import { useShallow } from 'zustand/react/shallow';

import { useProductModalOptions } from '~/hooks/product';
import { useProductStore } from '~/store';

import { ProductRegisterModalStockInfoSearchProductCodeModal } from '..';

import type { TypeProductModalRequiredInputState } from '~/types/product.types';

import styles from './ProductRegisterModalStockInfoDefault.module.scss';

const ProductRegisterModalStockInfoDefault = () => {
  const productModalOptions = useProductModalOptions();

  const [
    productRegisterModalValue,
    setProductRegisterModalValue,
    productModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    setFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productRegisterModalValue,
      state.setProductRegisterModalValue,
      state.productModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.setFocusFieldRefs,
    ])
  );

  const [isOpenProductCodeSearchModal, setIsOpenProductCodeSearchModal] = useState(false);

  const handleClickRequiredInput = (e: React.MouseEvent) => {
    resetProductModalRequiredValueStatus(e.currentTarget.id as keyof TypeProductModalRequiredInputState);
  };

  const handleClickCancelModal = () => {
    setIsOpenProductCodeSearchModal(false);
  };

  const handleClickConfirmModal = () => {
    setIsOpenProductCodeSearchModal(false);
  };

  const handleChangeProductTypeSelect = (_: unknown, value: TypeSelectOption<string>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      productType: value,
      productCode: '',
    });

    resetProductModalRequiredValueStatus(['productType', 'alternativeCode', 'productCode']);
  };

  const handleChangeAlternativeCodeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      alternativeCode: e.target.value,
    });

    resetProductModalRequiredValueStatus('alternativeCode');
  };

  const handleBlurInput = (e: React.FocusEvent<HTMLInputElement>) => {
    setProductRegisterModalValue({
      ...productRegisterModalValue,
      [e.currentTarget.id]: e.target.value.trim(),
    });
  };

  const handleClickSearchButton = () => {
    setIsOpenProductCodeSearchModal(true);
  };

  return (
    <>
      <div className={styles['item-row']}>
        <Label className={styles['label']} title="상품 운영 종류" required />
        <div className={styles['input-wrapper']}>
          <Select
            ref={(el) => setFocusFieldRefs('productType', el)}
            id="productType"
            options={productModalOptions?.productOperationType ?? []}
            size="small"
            value={productRegisterModalValue.productType}
            status={productModalRequiredValueStatus.productType.status}
            validationMessage={productModalRequiredValueStatus.productType.message}
            onChange={handleChangeProductTypeSelect}
          />
        </div>
      </div>

      {productRegisterModalValue.productType?.value === 'ORIGINAL' && (
        <div className={styles['item-row']}>
          <Label className={styles['label']} title="상품 바코드" required />
          <div className={styles['alt-code-input-wrapper']}>
            <Input
              ref={(el) => setFocusFieldRefs('alternativeCode', el)}
              id="alternativeCode"
              className={styles['input']}
              value={productRegisterModalValue.alternativeCode}
              type="text"
              size="small"
              name="barcode"
              count
              maxLength={50}
              placeholder="상품 바코드(88코드) 입력"
              status={productModalRequiredValueStatus.alternativeCode.status}
              validationMessage={productModalRequiredValueStatus.alternativeCode.message}
              onClick={handleClickRequiredInput}
              onChange={handleChangeAlternativeCodeInput}
              onBlur={handleBlurInput}
            />
          </div>
        </div>
      )}

      {productRegisterModalValue.productType?.value === 'ALTERNATIVE' && (
        <div className={styles['item-row']}>
          <Label className={styles['label']} title="출하코드" required />
          <div className={styles['product-code-input-wrapper']}>
            <Input
              ref={(el) => setFocusFieldRefs('productCode', el)}
              id="productCode"
              className={styles['input']}
              type="text"
              size="small"
              value={productRegisterModalValue.productCode}
              status={productModalRequiredValueStatus.productCode.status}
              validationMessage={productModalRequiredValueStatus.productCode.message}
              disabled
            />
            <Button className={styles['button']} size="small" color="secondary" onClick={handleClickSearchButton}>
              조회
            </Button>
          </div>
        </div>
      )}

      {isOpenProductCodeSearchModal && (
        <ProductRegisterModalStockInfoSearchProductCodeModal
          open={isOpenProductCodeSearchModal}
          onCancel={handleClickCancelModal}
          onConfirm={handleClickConfirmModal}
        />
      )}
    </>
  );
};

export default ProductRegisterModalStockInfoDefault;
